<template>
  <content-subpage
    class="add-from-library-content-subpage add-website"
    :visible="visible"
    :has-parent-subpage="hasParentSubpage"
    :titles="titles"
    :show-content-title="true"
    condensed
    header-sticky
    @content-subpage-hidden="actionHidden"
  >
    <template #main-content>
      <search-bar
        kind="white"
        active
        filter-type="selection"
        :keyword="searchPageParams.keyword"
        :placeholder="t('browse_or_search_resource_websites_placeholder')"
        @search="handleKeywordSearch"
      />
    </template>
    <template #main-content-bg>
      <template v-if="!hasKeyword">
        <card-list v-if="isLoading">
          <resource-search-card v-for="n in 1" :key="n" loading />
        </card-list>
        <empty-placeholder v-else-if="isEmptySearchResult">
          <template #icon>
            <generic-empty />
          </template>
          {{ emptyBrowseResultMessage }}
          <template v-if="emptyBrowseResultSubMessage" #sub>
            {{ emptyBrowseResultSubMessage }}
          </template>
        </empty-placeholder>
        <card-list v-else size="large">
          <resource-card
            v-for="item in searchResult.data"
            :key="item.uuid"
            v-bind="populateResourceCardProps(item)"
            v-on="populateResourceCardListeners(item)"
          />
        </card-list>
        <cv-pagination
          v-if="showPagination"
          :page="searchPageParams.pageNo"
          :page-sizes="[searchPageParams.pageSize]"
          :forwards-button-disabled="disableForwardsButton"
          :backwards-button-disabled="disableBackwardsButton"
          @change="handlePageChange"
        />
      </template>
      <div v-else class="search-result-wrapper">
        <card-list v-if="isLoading">
          <resource-search-card v-for="n in 20" :key="n" :loading="isLoading" />
        </card-list>
        <template v-else>
          <div class="header">
            <span>{{ totalRecordLabel }}</span>
            <dropdown-component
              :label="t('search_result_sort_by_label')"
              :value="searchPageParams.sortBy"
              class="sort-by-filter"
              @change="handleSortByChange"
            >
              <cv-dropdown-item
                v-for="option in sortByOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.name }}
              </cv-dropdown-item>
            </dropdown-component>
          </div>
          <empty-placeholder v-if="isEmptySearchResult">
            <template #icon>
              <search-empty />
            </template>
            {{ emptySearchResultMessage }}
            <template #sub>
              {{ emptySearchResultSubMessage }}
            </template>
          </empty-placeholder>
          <card-list v-else size="">
            <resource-search-card
              v-for="item in searchResult.data"
              :key="item.uuid"
              v-bind="populateResourceSearchCardProps(item)"
              v-on="populateResourceSearchCardListeners(item)"
            />
          </card-list>
          <cv-pagination
            v-if="showPagination"
            :page="searchPageParams.pageNo"
            :page-sizes="[searchPageParams.pageSize]"
            :forwards-button-disabled="disableForwardsButton"
            :backwards-button-disabled="disableBackwardsButton"
            @change="handlePageChange"
          />
        </template>
      </div>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import { onBeforeMount, ref } from "vue";
import ResourceSearchCard from "@/components/ResourceSearchCard.vue";
import EmptyPlaceholder from "@plugins/common/components/EmptyPlaceholder.vue";
import SearchBar from "@plugins/common/components/SearchBar.vue";
import CardList from "@plugins/card/components/CardList.vue";
import ContentSubpage from "@plugins/content-subpage/components/ContentSubpage.vue";
import {
  ContentSubpageParams,
  useContentSubpage,
} from "@plugins/content-subpage/composables/content-subpage.ts";
import { useI18n } from "vue-i18n";
import { ResourceCardProps } from "@/common/card-types.ts";
import { useAddFromLibrarySubpage } from "@modules/resource-search/composables/use-add-from-library-subpage.ts";
import {
  AddWebsiteFromLibraryContentSubpageSelectionEvent,
  ResourceSearchParams,
  WebsiteResultItem,
} from "@modules/resource-search/resource-search-types.ts";
import {
  BROWSE_RESULT_PAGE_SIZE,
  RESOURCE_SEARCH_TYPE,
} from "@modules/resource-search/resource-search-helper.ts";
import DropdownComponent from "@plugins/common/components/DropdownComponent.vue";
import GenericEmpty from "@images/placeholder/empty-states/GenericEmpty.svg";
import SearchEmpty from "@images/placeholder/empty-states/SearchEmpty.svg";
import ResourceCard from "@/components/ResourceCard.vue";
import { LOCATION_TYPE_VALUE } from "@modules/search/service/search.constant.ts";
import { useWebsiteCard } from "@modules/resource-browse/composables/use-website-card.ts";
import { useWebsiteSearchCard } from "@modules/resource-search/composables/use-website-search-card.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";
import EmbedWebsiteSelectionContentSubpage from "@modules/editor/components/lesson/EmbedWebsiteSelectionContentSubpage.vue";

// Props
interface Props {
  params: ContentSubpageParams;
}

interface SelectedDomain {
  domainName: string;
  domainUuid: string;
  embeddedType: string;
}

const { params: props } = defineProps<Props>();

const emit = defineEmits<{
  selectionSaved: [data: AddWebsiteFromLibraryContentSubpageSelectionEvent];
}>();

// State
const searchPageParams = ref<ResourceSearchParams>({
  keyword: "",
  location: [LOCATION_TYPE_VALUE.EXTERNAL_RESOURCES],
  pageNo: 1,
  pageSize: BROWSE_RESULT_PAGE_SIZE,
  resource: RESOURCE_SEARCH_TYPE.WEBSITE,
});

// Computed

// Composable
const { t } = useI18n();
const { passiveApiFailHandler } = useErrorHandler();
const { populateWebsiteCardProps } = useWebsiteCard();
const { populateWebsiteSearchCardProps } = useWebsiteSearchCard();
const contentSubpageInstance = useContentSubpageInstance();

// Methods
const getSelectionEvent = (
  item: SelectedDomain,
): AddWebsiteFromLibraryContentSubpageSelectionEvent => {
  return {
    domainSelection: {
      domainName: item.domainName,
      domainUuid: item.domainUuid,
      embeddedType: item.embeddedType,
    },
    onSuccess: () => {
      contentSubpageInstance.removeAll();
    },
    onFailure: () => {
      passiveApiFailHandler();
    },
  };
};

const handleOnWebsiteSelected = (item: SelectedDomain) => {
  emit("selectionSaved", getSelectionEvent(item));
};

const handleOnWebsiteCardClick = (item: WebsiteResultItem) => {
  contentSubpageInstance.show(
    EmbedWebsiteSelectionContentSubpage,
    {
      hasParentSubpage: true,
      baseTitle: [...props.baseTitle, t("embed_website_browse_websites_title")],
      title: item.name,
      websiteUuid: item.uuid,
      highlights: item.highlights,
      actionBack: () => {
        //empty
      },
    },
    { onWebsiteSelected: handleOnWebsiteSelected },
  );
};

const populateResourceCardProps = (item: WebsiteResultItem): ResourceCardProps => {
  return populateWebsiteCardProps(item, false);
};

const populateResourceCardListeners = (item: WebsiteResultItem): { click: () => void } => {
  return {
    click: () => handleOnWebsiteCardClick(item),
  };
};

const populateResourceSearchCardProps = (item: WebsiteResultItem) => {
  return populateWebsiteSearchCardProps(item, false);
};

const populateResourceSearchCardListeners = (item: WebsiteResultItem): { click: () => void } => {
  return {
    click: () => handleOnWebsiteCardClick(item),
  };
};

const triggerSearch = async () => {
  isLoading.value = true;
  await fetchSearchData();
  isLoading.value = false;
};

const triggerSearchAndAggregateFilters = async () => {
  isLoading.value = true;
  await fetchSearchData();
  isLoading.value = false;
};

// Composables
const {
  disableForwardsButton,
  disableBackwardsButton,
  emptyBrowseResultMessage,
  emptyBrowseResultSubMessage,
  emptySearchResultMessage,
  emptySearchResultSubMessage,
  hasKeyword,
  isLoading,
  isEmptySearchResult,
  searchResult,
  sortByOptions,
  totalRecordLabel,
  showPagination,
  fetchSearchData,
  handlePageChange,
  handleKeywordSearch,
  handleSortByChange,
} = useAddFromLibrarySubpage(
  searchPageParams,
  props,
  {},
  triggerSearch,
  triggerSearchAndAggregateFilters,
);

// Content Subpage setup
const { hasParentSubpage, titles, actionHidden, visible, defaultExpose } = useContentSubpage(props);
defineExpose(defaultExpose);

// Lifecycle hooks
onBeforeMount(async () => {
  isLoading.value = true;
  await fetchSearchData();
  isLoading.value = false;
});

titles.value.push(t("embed_website_browse_websites_title"));
</script>
